




























































































































































































import { AxiosError } from 'axios';
// utils helpers
import mixins from '@/utils/mixins';
import { mapActions } from 'vuex';
import { cloneDeep } from 'lodash';

// mixins
import componentState from '@/mixins/state';
import { provide as RegistrableProvide } from '@/mixins/registrable';

import { ALForm } from '@/common/component.types';

import {
  IQuoteServiceTemplateData,
  IQuoteTemplate,
  QuoteServiceTemplate,
  QuoteServiceTemplateFieldError,
  QuoteTemplateTranslationLanguage
} from '@/models';

import { ErrorManager } from '@/models/error';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import QuoteServiceTemplateComputationList from '@/components/quotes/templates/services/QuoteServiceTemplateComputationList.vue';
import QuoteServiceVariableSearch from '@/components/quoteservicevariable/QuoteServiceVariableSearch.vue';
import { deepEqual, diffDeep } from '@/utils/helpers';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import QuillPDFEditor from '@/components/common/QuillPDFEditor.vue';
import QuoteTemplateNoteField from '../common/QuoteTemplateNoteField.vue';

type QuoteServiceVariableSearchInstance = InstanceType<
  typeof QuoteServiceVariableSearch
>;

const baseMixins = mixins(componentState, RegistrableProvide('template'));

export interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    form: ALForm;
  };
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    components: {
      ErrorAlert,
      QuoteServiceTemplateComputationList,
      ConfirmationDialog,
      ALDialog,
      QuillPDFEditor,
      QuoteTemplateNoteField
    },
    name: 'QuoteServiceFormDialog',

    provide(): { template: IQuoteTemplate } {
      return {
        template: this.quoteServiceTemplate
      };
    },

    data() {
      return {
        currentTab: 0,
        valid: false,
        dialog: false,
        createForm: false,

        discardUnsavedDataDialog: false,

        loading: false,

        quoteServiceTemplate: new QuoteServiceTemplate(),
        initialQuoteServiceTemplate: null as null | QuoteServiceTemplate,
        quoteServiceTemplateTranslation: new QuoteServiceTemplate(),

        titleRules: [
          (v: any) => !!v || 'Title is required',
          (v: any) =>
            (v && v.length <= 128) || 'Title must be at most 128 characters'
        ],

        errorMessage: '',
        detailErrorMessage: '',
        fieldError: new QuoteServiceTemplateFieldError(),

        currentLanguage: QuoteTemplateTranslationLanguage.en,
        QuoteTemplateTranslationLanguage: QuoteTemplateTranslationLanguage,

        languagesList: [
          {
            text: 'English',
            value: QuoteTemplateTranslationLanguage.en
          },
          {
            text: 'Spanish',
            value: QuoteTemplateTranslationLanguage.es
          },
          {
            text: 'Russian',
            value: QuoteTemplateTranslationLanguage.ru
          }
        ],

        requiredRules: [(v: string) => !!v || 'This field is required']
      };
    },

    methods: {
      ...mapActions({
        updateQuoteServiceTemplate: 'quoteTemplate/updateQuoteServiceTemplate',
        createQuoteServiceTemplate: 'quoteTemplate/createQuoteServiceTemplate',

        fetchQuoteServiceTemplateTranslations:
          'quoteTemplate/fetchQuoteServiceTemplateTranslations',
        createQuoteServiceTemplateTranslation:
          'quoteTemplate/createQuoteServiceTemplateTranslation',
        updateQuoteServiceTemplateTranslation:
          'quoteTemplate/updateQuoteServiceTemplateTranslation',

        fetchQuoteServiceVariables:
          'quoteServiceVariable/fetchQuoteServiceVariables'
      }),

      resetFormValidation(): void {
        if (this.$refs.form) {
          return this.$refs.form.resetValidation();
        }
      },

      validateForm(): boolean {
        let valid = false;
        if (this.$refs.form) {
          valid = this.$refs.form.validate();
        }
        return valid;
      },

      register(node: QuoteServiceVariableSearchInstance) {
        node.serviceTemplateId = this.quoteServiceTemplate.id ?? null;
      },
      unregister(node: QuoteServiceVariableSearchInstance) {
        node.serviceTemplateId = null;
      },

      clearAllErrors(): void {
        this.errorMessage = '';
        this.detailErrorMessage = '';
        this.fieldError.clearErrors();
      },

      open(quoteServiceTemplate: QuoteServiceTemplate, createForm: boolean) {
        try {
          this.clearAllErrors();
          this.currentLanguage = QuoteTemplateTranslationLanguage.en;

          this.initialQuoteServiceTemplate = quoteServiceTemplate;
          this.quoteServiceTemplate = cloneDeep(quoteServiceTemplate);

          this.quoteServiceTemplateTranslation = new QuoteServiceTemplate();
          this.createForm = createForm;
          this.fetchQuoteServiceVariables();
          this.dialog = true;
          this.setComponentState('loaded');
          this.resetFormValidation();
        } catch (err) {
          this.setComponentState('error', err);
        }
      },

      close() {
        if (
          !deepEqual(
            this.initialQuoteServiceTemplate,
            this.quoteServiceTemplate
          )
        ) {
          this.discardUnsavedDataDialog = true;
        } else {
          this.closeDialog();
        }
      },

      closeUnsavedDataDialog() {
        this.discardUnsavedDataDialog = false;
      },

      closeDialog() {
        this.dialog = false;
        this.$nextTick(() => {
          this.createForm = false;
        });
      },

      async getQuoteTemplateTranslation(
        language: QuoteTemplateTranslationLanguage
      ) {
        this.clearAllErrors();
        try {
          this.quoteServiceTemplateTranslation = new QuoteServiceTemplate();
          if (!this.quoteServiceTemplate.id) {
            return;
          }
          await this.fetchQuoteServiceTemplateTranslations(
            this.quoteServiceTemplate.id
          ).then((templates: IQuoteServiceTemplateData[]) => {
            const quoteServiceTemplate = templates.find(template => {
              return template.language === language;
            });
            if (quoteServiceTemplate) {
              this.quoteServiceTemplateTranslation = new QuoteServiceTemplate(
                quoteServiceTemplate
              );
            }
          });
        } catch (err) {
          const error = err as AxiosError;
          if (error.response) {
            if (
              QuoteServiceTemplateFieldError.isQuoteServiceTemplateFieldError(
                error.response.data
              )
            ) {
              this.fieldError = new QuoteServiceTemplateFieldError(
                error.response.data
              );
            } else if (error.response.data.detail) {
              this.detailErrorMessage = error.response.data.detail;
            }
          }

          this.errorMessage = ErrorManager.extractApiError(error);
        } finally {
          this.loading = false;
        }
      },

      async save() {
        if (this.validateForm()) {
          this.clearAllErrors();
          this.loading = true;

          try {
            this.quoteServiceTemplateTranslation.language = this.currentLanguage;
            switch (this.currentLanguage) {
              case QuoteTemplateTranslationLanguage.es:
              case QuoteTemplateTranslationLanguage.ru:
                await this.fetchQuoteServiceTemplateTranslations(
                  this.quoteServiceTemplate.id
                ).then((templates: IQuoteServiceTemplateData[]) => {
                  const quoteServiceTemplate = templates.find(template => {
                    return (
                      template.language ===
                      this.quoteServiceTemplateTranslation.language
                    );
                  });

                  if (quoteServiceTemplate) {
                    this.updateQuoteServiceTemplateTranslation({
                      templateId: this.quoteServiceTemplate.id,
                      translation: this.quoteServiceTemplateTranslation
                    }).then((template: IQuoteServiceTemplateData) => {
                      this.quoteServiceTemplateTranslation = new QuoteServiceTemplate(
                        template
                      );
                      this.closeDialog();
                    });
                  } else {
                    this.createQuoteServiceTemplateTranslation({
                      templateId: this.quoteServiceTemplate.id,
                      translation: this.quoteServiceTemplateTranslation
                    }).then((template: IQuoteServiceTemplateData) => {
                      this.quoteServiceTemplateTranslation = new QuoteServiceTemplate(
                        template
                      );
                      this.closeDialog();
                    });
                  }
                });

                break;
              case QuoteTemplateTranslationLanguage.en:
              default:
                if (this.createForm) {
                  await this.createQuoteServiceTemplate(
                    this.quoteServiceTemplate
                  ).then(() => this.closeDialog());
                } else {
                  const quoteServiceTemplateDelta = diffDeep(
                    this.quoteServiceTemplate,
                    this.initialQuoteServiceTemplate,
                    true
                  );
                  await this.updateQuoteServiceTemplate(
                    quoteServiceTemplateDelta
                  ).then(() => this.closeDialog());
                }
            }
          } catch (err) {
            const error = err as AxiosError;
            if (error.response) {
              if (
                QuoteServiceTemplateFieldError.isQuoteServiceTemplateFieldError(
                  error.response.data
                )
              ) {
                this.fieldError = new QuoteServiceTemplateFieldError(
                  error.response.data
                );
              } else if (error.response.data.detail) {
                this.detailErrorMessage = error.response.data.detail;
              }
            }

            this.errorMessage = ErrorManager.extractApiError(error);
          } finally {
            this.loading = false;
          }
        }
      }
    }
  });
